import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import { Badge } from "components/ui/badge";
import { CheckCircle, Warning, MapPin } from "@phosphor-icons/react";
import { API_URL } from "constants/index";

interface AssetPublicInfo {
  id: string;
  name: string;
  categoryName?: string;
  buildingAddress?: string;
  unitName?: string;
  location?: string;
}

export const ReportAssetIssuePage = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const [asset, setAsset] = useState<AssetPublicInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [description, setDescription] = useState("");
  const [reporterName, setReporterName] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");
  const [reporterPhone, setReporterPhone] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAsset = async () => {
      if (!assetId) return;
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/assets/public/${assetId}`);
        if (!response.ok) {
          throw new Error("Voorziening niet gevonden");
        }
        const data = await response.json();
        setAsset(data);
      } catch (error) {
        setLoadError(
          error instanceof Error
            ? error.message
            : "Kon voorziening niet laden"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchAsset();
  }, [assetId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await fetch(`${API_URL}/helpdesk/public/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          assetId,
          description: description.trim(),
          ...(reporterName ? { reporterName } : {}),
          ...(reporterEmail ? { reporterEmail } : {}),
          ...(reporterPhone ? { reporterPhone } : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || "Kon melding niet versturen"
        );
      }

      setSubmitted(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Er is een fout opgetreden"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-muted-foreground">Laden...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center shadow-sm">
          <Warning className="mx-auto mb-3 h-12 w-12 text-red-500" />
          <h1 className="text-lg font-semibold">Niet gevonden</h1>
          <p className="mt-2 text-sm text-muted-foreground">{loadError}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg border bg-white p-6 text-center shadow-sm">
          <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-500" />
          <h1 className="text-lg font-semibold">Melding ingediend</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Bedankt voor uw melding. We nemen zo snel mogelijk contact met u op.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-6">
          <h1 className="text-lg font-semibold">Probleem melden</h1>
          {asset && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{asset.name}</span>
                {asset.categoryName && (
                  <Badge variant="secondary">{asset.categoryName}</Badge>
                )}
              </div>
              {(asset.buildingAddress || asset.location) && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {[asset.buildingAddress, asset.location]
                    .filter(Boolean)
                    .join(" - ")}
                </div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description">
              Beschrijving van het probleem <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Beschrijf het probleem zo duidelijk mogelijk..."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Uw naam (optioneel)</Label>
            <Input
              id="name"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
              placeholder="Naam"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres (optioneel)</Label>
            <Input
              id="email"
              type="email"
              value={reporterEmail}
              onChange={(e) => setReporterEmail(e.target.value)}
              placeholder="uw@email.be"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefoonnummer (optioneel)</Label>
            <Input
              id="phone"
              type="tel"
              value={reporterPhone}
              onChange={(e) => setReporterPhone(e.target.value)}
              placeholder="+32 ..."
            />
          </div>

          {submitError && (
            <p className="text-sm text-red-500">{submitError}</p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !description.trim()}
          >
            {isSubmitting ? "Versturen..." : "Melding versturen"}
          </Button>
        </form>
      </div>
    </div>
  );
};
